import React, { type FC, type BaseSyntheticEvent, useCallback, useMemo, useState } from "react";
import type ReCaptcha from "react-google-recaptcha";
import { type FieldValues, type SubmitHandler } from "react-hook-form";
import { Toast, useToast } from "@/components/essentials/toast";
import { datadogLogs } from "@datadog/browser-logs";
import { Typography } from "@/components/essentials/typography";
import { Box } from "@/components/essentials/box";
import Link from "next/link";

export const useErrorHandler = (): ((e?: unknown) => void) => {
  const toast = useToast();

  const errorHandler = useCallback(
    (e?: unknown): void => {
      datadogLogs.logger.error("reCAPTCHA-api-error", e as object);
      toast({
        render: () => <Toast variant="error">予期せぬエラーが発生しました。</Toast>,
      });
    },
    [toast],
  );

  return errorHandler;
};

type SimpleSubmitHandler<TFieldValues extends FieldValues> = (
  data: TFieldValues,
  event?: BaseSyntheticEvent,
) => unknown;

export const useReCaptcha = (): {
  isRecaptchaLoaded: boolean;
  withReCaptcha: <TFieldValues extends FieldValues>(submit: SubmitHandler<TFieldValues>) => SubmitHandler<TFieldValues>;
  reCaptchaProps: {
    ref: React.RefObject<ReCaptcha>;
    sitekey: string;
    onChange: () => void;
    asyncScriptOnLoad: () => void;
  };
  PolicySentence: FC;
} => {
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState<boolean>(false);
  const reCaptchaRef = React.useRef<ReCaptcha>(null);
  const tokenRef = React.useRef<string | null>(null);

  const errorHandler = useErrorHandler();

  const asyncScriptOnLoad = useCallback(() => {
    setIsRecaptchaLoaded(true);
  }, []);

  const withReCaptcha = useCallback(
    <TFieldValues extends FieldValues>(
      submit: SimpleSubmitHandler<TFieldValues & { token: string }>,
    ): SimpleSubmitHandler<TFieldValues> =>
      async (data, event) => {
        // biome-ignore lint/style/useBlockStatements :[AUTOMATICALLY-INSERTED] Block statements are preferred in this position.
        if (reCaptchaRef.current === null || !isRecaptchaLoaded) return;

        try {
          if (tokenRef.current === null) {
            // executeAsync: 一度取得した token が expired になる前に呼び出すと、処理が止まってしまう
            tokenRef.current = await reCaptchaRef.current.executeAsync();
          }
        } catch (error: unknown) {
          errorHandler(error);
          return;
        }
        if (tokenRef.current === null) {
          errorHandler();
          return;
        }

        await submit({ ...data, token: tokenRef.current }, event);
      },
    [errorHandler, isRecaptchaLoaded],
  );

  const clearExpiredToken = useCallback(() => {
    tokenRef.current = null;
  }, []);

  const reCaptchaProps = useMemo(() => {
    if (typeof process.env.RECAPTCHA_SITE_KEY !== "string") {
      throw new Error("You need to provide a reCaptcha site key as `RECAPTCHA_SITE_KEY` env variable.");
    }
    return {
      ref: reCaptchaRef,
      sitekey: process.env.RECAPTCHA_SITE_KEY,
      onChange: clearExpiredToken,
      asyncScriptOnLoad,
    };
  }, [asyncScriptOnLoad, clearExpiredToken]);

  const PolicySentence = (): JSX.Element => {
    return (
      <Box my="2.5rem" w="100%" textAlign="center">
        <Typography variant="text4" textAlign="center" color="gray.dark">
          このサイトはreCAPTCHAで保護されており、Googleの
          <Link href="https://policies.google.com/privacy" passHref>
            プライバシーポリシー
          </Link>
          と
          <Link href="https://policies.google.com/terms" passHref>
            利用規約
          </Link>
          が適用されます。
        </Typography>
      </Box>
    );
  };

  return {
    isRecaptchaLoaded,
    withReCaptcha,
    reCaptchaProps,
    PolicySentence,
  };
};
